import React, { useContext } from 'react';
import MediaQuery from 'react-responsive';
import styled from 'styled-components';
import { Box, Heading, Stack, SubtleText } from '@99designs/emu';
import {
    borders,
    boxShadows,
    breakpoints,
    colors,
    spaces,
    typography,
} from '@99designs/emu/settings';
import { CurrencyCode } from '@99designs/i18n';
import { IconTick1 } from '@99designs/icons';
import {
    PromoBarArtwork,
    SquareProductLogo,
    SquarespaceProductLogo,
    WixProductLogo,
} from '@99designs/spa-common';
import { ViewProductDetails } from '@99designs/spa-common';
import { Partner, PartnerContext } from '@99designs/spa-common';
import { Price } from '@99designs/spa-common';
import { getViewerRole, useViewer } from '@99designs/viewer';
import { BriefContext } from '../BriefContext';
import { BriefOverviewProductFragment } from '../BriefOverview/BriefOverview.generated';
import { PartnerLogo } from '../PartnerLogo';

export interface BriefPackageProps {
    product: BriefOverviewProductFragment;
    collapsed?: boolean;
    showPartnerLogo?: boolean;
}

const toRightGradientVibrantBlue = `linear-gradient(to right, #3EC1F3, #4856DF);`;

const Card = styled.div`
    overflow: hidden;
    border-radius: ${borders.borderRadiuses.medium}px;
    box-shadow: ${boxShadows.medium};
`;

const CardHeading = styled.div<{ background?: string }>`
    background-image: ${({ background }) => (background ? background : `url(${PromoBarArtwork})`)};
    background-color: ${(props) => props.color};
    background-size: cover;
    padding: ${spaces['1bu']};
`;

const Color = styled.div`
    color: ${(props) => props.color};
`;

const PriceLabel = styled.span`
    font-size: ${typography.typeScale.size4}px;
`;

const FixedPricePackage = styled.strong`
    color: ${colors.grey700};
`;

const StyledPackageItem = styled.div`
    display: grid;
    grid-template-columns: auto 1fr;
    padding-bottom: ${spaces['1bu']};
    padding-top: ${spaces['1bu']};

    & + & {
        border-top: ${borders.content};
    }
`;

export const PackageItem = ({ children }: { children: React.ReactNode }) => (
    <StyledPackageItem>
        <Color color={colors.semantic.success}>
            <IconTick1 />
        </Color>
        <Box marginLeft="1bu">{children}</Box>
    </StyledPackageItem>
);

function partnerLogo(partner?: Partner) {
    if (!partner) {
        return;
    }

    const partnerLogoMap: Partial<{ [partner in Partner]: string }> = {
        Squarespace: SquarespaceProductLogo,
        Square: SquareProductLogo,
        Wix: WixProductLogo,
    };

    return partnerLogoMap[partner];
}

export function BriefPackage({ product, collapsed, showPartnerLogo = false }: BriefPackageProps) {
    const {
        pricing: { amount, earningsInUsd },
        title,
        description,
        details,
        disclaimer,
        additionalDetails,
        image,
        headerVideo,
    } = product;
    const viewer = useViewer();
    const isDesigner = getViewerRole(viewer) === 'designer';
    const priceAmount = isDesigner ? earningsInUsd : amount.amountInCents;
    const displayCurrency = isDesigner ? CurrencyCode.USD : (amount.currency as CurrencyCode);
    const { paymentSource, noPaymentRequiredHeading } = useContext(BriefContext);
    const { partner, color } = useContext(PartnerContext);

    return (
        <Card data-testid="package">
            {(paymentSource === 'fully-funded' || paymentSource === 'paid') && (
                <CardHeading
                    data-testid="funded-heading"
                    color={color}
                    background={paymentSource === 'paid' ? toRightGradientVibrantBlue : undefined}
                >
                    {noPaymentRequiredHeading && (
                        <Color color="white">
                            <Heading appearanceLevel={5} accessibilityLevel={5}>
                                {noPaymentRequiredHeading}
                            </Heading>
                        </Color>
                    )}
                </CardHeading>
            )}
            <Box padding="1bu">
                <Stack space="1bu">
                    <MediaQuery maxWidth={breakpoints.medium[0]}>
                        {(isMobile) => (
                            <Box
                                display="flex"
                                alignItems={
                                    isMobile || paymentSource !== 'paid' ? 'flex-end' : 'center'
                                }
                            >
                                <Stack space="0.5bu">
                                    {showPartnerLogo && partner && (
                                        <PartnerLogo partner={partner} />
                                    )}
                                    <Heading appearanceLevel={4} accessibilityLevel={4}>
                                        {title}
                                    </Heading>
                                    <PriceLabel>
                                        <Price
                                            amount={priceAmount}
                                            currency={displayCurrency}
                                            color={
                                                paymentSource === 'paid' ? colors.pacific : color
                                            }
                                            paymentSource={paymentSource}
                                        />
                                    </PriceLabel>
                                </Stack>
                            </Box>
                        )}
                    </MediaQuery>
                    {!collapsed && (
                        <Box>
                            <Stack space="0.5bu">
                                <FixedPricePackage>{__('Fixed price package')}</FixedPricePackage>
                                <div>{description}</div>
                                <div>
                                    {details.map(({ title }) => (
                                        <PackageItem key={title}>{title}</PackageItem>
                                    ))}
                                </div>
                                {disclaimer && <SubtleText>{disclaimer}</SubtleText>}
                            </Stack>
                        </Box>
                    )}
                    <Box>
                        <ViewProductDetails
                            features={details}
                            additionalDetails={additionalDetails}
                            productSummary={{
                                title,
                                description: description,
                                logo: partnerLogo(partner),
                                fromPrice: {
                                    amountInCents: priceAmount,
                                    currency: displayCurrency,
                                },
                                showFixedPrice: true,
                            }}
                            linkText={__("What's included and extra costs")}
                            image={image}
                            headerVideo={headerVideo}
                        />
                    </Box>
                </Stack>
            </Box>
        </Card>
    );
}
